import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { io, Socket } from "socket.io-client";
import { SOCKET_IO_PATH, WSS_URL } from "../../utils/env";
import {
  IWsServerAck,
  IWsServerError,
  subscribePayloadFor,
  unsubscribePayloadFor,
} from "../../contexts/WebsocketAuthContext/subscriptionManager";
import { parseSocketPayload } from "./parseFrame";
import { createFrameBuffer } from "./frameBuffer";
import { RealtimeError } from "./realtimeError";

// Shared websocket using Socket.IO.
// Socket.IO provides automatic reconnection and fallback transports.
//
// Frames are parsed ONCE here (FE-11): every buffered MessageEvent's `data` is
// the decoded object, never a JSON string, and an unparsable frame is dropped
// and reported instead of reaching a hook. Hooks read them with `readFrame`.

const flushMessagesIntervalMs = 250;

/**
 * Reconnection never gives up (FE-2): exponential backoff from 1 s to 30 s with
 * ±50% jitter, so a backend restart does not get every tab back at once.
 */
export const SOCKET_RECONNECTION_OPTIONS = {
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 30000,
  randomizationFactor: 0.5,
} as const;

/** `connecting` before the first connect, `reconnecting` after a drop, `closed` only when we closed it. */
export type ConnectionStatus = "connecting" | "open" | "reconnecting" | "closed";

export type ServerAckEvent = "subscribed" | "unsubscribed";

export interface SharedWebsocketHandlers {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  /** Raw payload of every `message` event (the auth reply arrives here). */
  onMessage?: (message: unknown) => void;
  /** Payload of a server `error` event. */
  onServerError?: (error: IWsServerError | undefined) => void;
  /** Payload of a `subscribed` / `unsubscribed` reply. */
  onServerAck?: (ack: IWsServerAck | undefined, event: ServerAckEvent) => void;
  /** Channels that lost buffered frames to a cap since the last flush. */
  onFramesDropped?: (channels: string[]) => void;
}

// Convert ws:// URL to http:// for Socket.IO
const getSocketIOUrl = (url: string) => {
  return url.replace(/^ws:\/\//, 'http://').replace(/^wss:\/\//, 'https://');
};

// WebSocket-compatible ready states
const ReadyState = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const;

// Events that are lifecycle or handled explicitly, never buffered as channel frames.
// `error`, `subscribed` and `unsubscribed` are excluded because their payloads
// carry the channel they are about, which would otherwise be delivered to that
// channel's consumers as if it were data.
const NON_CHANNEL_EVENTS = new Set([
  "connect",
  "disconnect",
  "message",
  "error",
  "subscribed",
  "unsubscribed",
]);

let sharedSocket: Socket | null = null;
let socketRefCount = 0;

const breadcrumb = (
  message: string,
  data?: Record<string, unknown>,
  level: "info" | "warning" = "info"
) => {
  Sentry.addBreadcrumb({ category: "websocket", message, data, level });
};

/**
 * Reconnecting after the server kicked the socket ("io server disconnect"),
 * which Socket.IO leaves to the caller. It backs off like the built-in
 * reconnection: a server that refuses the connection (for example over its
 * per-address connection cap) kicks again at once, so reconnecting immediately
 * looped as fast as the network allowed. One timer for the shared socket.
 */
let serverKickTimer: ReturnType<typeof setTimeout> | null = null;
let serverKickAttempts = 0;
let stableConnectionTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * A refused connection still connects first (the server sends CONNECT before its
 * connection handler kicks), so attempts reset only after a connection that
 * stayed up this long, never on `connect` itself.
 */
export const STABLE_CONNECTION_MS = 10_000;

export const serverKickReconnectDelayMs = (
  attempt: number,
  random: () => number = Math.random
) => {
  const { reconnectionDelay, reconnectionDelayMax, randomizationFactor } =
    SOCKET_RECONNECTION_OPTIONS;
  const base = Math.min(
    reconnectionDelayMax,
    reconnectionDelay * 2 ** Math.min(Math.max(attempt, 0), 30)
  );
  const jitter = base * randomizationFactor * (random() * 2 - 1);
  return Math.round(Math.min(reconnectionDelayMax, base + jitter));
};

const clearStableConnectionTimer = () => {
  if (stableConnectionTimer) {
    clearTimeout(stableConnectionTimer);
    stableConnectionTimer = null;
  }
};

const cancelServerKickReconnect = (resetAttempts: boolean) => {
  if (serverKickTimer) {
    clearTimeout(serverKickTimer);
    serverKickTimer = null;
  }
  if (resetAttempts) {
    serverKickAttempts = 0;
    clearStableConnectionTimer();
  }
};

/** Connected: no kick-reconnect is needed, and a connection that lasts resets the backoff. */
const noteServerConnected = () => {
  inSocketIoBackoff = false;
  cancelServerKickReconnect(false);
  clearStableConnectionTimer();
  stableConnectionTimer = setTimeout(() => {
    stableConnectionTimer = null;
    serverKickAttempts = 0;
  }, STABLE_CONNECTION_MS);
};

const scheduleServerKickReconnect = (socket: Socket) => {
  // The connection that was just kicked did not last: keep the backoff growing.
  clearStableConnectionTimer();
  if (serverKickTimer) return;
  const delayMs = serverKickReconnectDelayMs(serverKickAttempts);
  serverKickAttempts += 1;
  breadcrumb("reconnect after server disconnect", {
    delayMs,
    attempt: serverKickAttempts,
  });
  serverKickTimer = setTimeout(() => {
    serverKickTimer = null;
    if (sharedSocket === socket && !socket.connected) socket.connect();
  }, delayMs);
};

/**
 * socket.io is waiting out its own reconnection backoff: set on a drop it
 * reconnects from by itself, cleared on connect. socket.io keeps that state
 * private, and ignores `connect()` while it lasts, so it is tracked here.
 */
let inSocketIoBackoff = false;
let lastForcedReconnectAt = -Infinity;

/**
 * Minimum gap between reconnects that skip a backoff (network back, tab
 * visible), so a burst of those events cannot hammer a per-address
 * connection cap.
 */
export const FORCED_RECONNECT_MIN_GAP_MS = 2_000;

/** The shared socket is gone: forget every reconnect in progress. */
const resetReconnectState = () => {
  cancelServerKickReconnect(true);
  inSocketIoBackoff = false;
  lastForcedReconnectAt = -Infinity;
};

/** Sentry exceptions from handler failures per page load, like the frame parser's budget (L2). */
export const MAX_HANDLER_REPORTS = 5;
let handlerReportsSent = 0;

/** Test hook: the report budget is per page load. */
export const resetHandlerReports = () => {
  handlerReportsSent = 0;
};

/**
 * Run a handler without letting it throw into socket.io's packet loop, where
 * an exception would break delivery for every later frame. Every failure
 * leaves a breadcrumb; only the first few become Sentry events, so a handler
 * that throws on every frame cannot flood it.
 */
const callHandler = (name: string, call: () => void) => {
  try {
    call();
  } catch (error) {
    breadcrumb("handler failed", { handler: name }, "warning");
    if (handlerReportsSent >= MAX_HANDLER_REPORTS) return;
    handlerReportsSent += 1;
    Sentry.captureException?.(error, {
      tags: { area: "realtime" },
      extra: { handler: name },
    });
  }
};

const asObject = <T>(payload: unknown): T | undefined =>
  payload && typeof payload === "object" ? (payload as T) : undefined;

// Create a MessageEvent-compatible object
const createMessageEvent = (data: any): MessageEvent<any> => {
  return {
    data,
    type: 'message',
    bubbles: false,
    cancelBubble: false,
    cancelable: false,
    composed: false,
    currentTarget: null,
    defaultPrevented: false,
    eventPhase: 0,
    isTrusted: true,
    returnValue: true,
    srcElement: null,
    target: null,
    timeStamp: Date.now(),
    lastEventId: '',
    origin: '',
    ports: [],
    source: null,
    composedPath: () => [],
    initEvent: () => {},
    preventDefault: () => {},
    stopImmediatePropagation: () => {},
    stopPropagation: () => {},
    AT_TARGET: 2,
    BUBBLING_PHASE: 3,
    CAPTURING_PHASE: 1,
    NONE: 0,
    initMessageEvent: () => {},
  } as MessageEvent<any>;
};

const useSharedWebsocket = (handlers: SharedWebsocketHandlers = {}) => {
  // Bounded and conflated per channel (FE-12): a throttled background tab
  // can no longer deliver one enormous batch on refocus.
  const data = useRef(
    createFrameBuffer<MessageEvent<any>>((event) => event.data)
  );
  const [readyState, setReadyState] = useState<number>(ReadyState.CONNECTING);
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("connecting");
  const socketRef = useRef<Socket | null>(null);
  // Latest handlers, read at event time so callers may pass a new object every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    // Use shared socket instance
    if (!sharedSocket) {
      const socketUrl = getSocketIOUrl(WSS_URL);
      try {
        sharedSocket = io(socketUrl, {
          path: SOCKET_IO_PATH,
          transports: ['websocket', 'polling'],
          ...SOCKET_RECONNECTION_OPTIONS,
          autoConnect: true,
        });
      } catch (error) {
        // Marked, so RealtimeErrorBoundary degrades to REST instead of the app crashing.
        throw new RealtimeError("Could not create the realtime socket", error);
      }
    }
    const socket = sharedSocket;

    const onConnect = () => {
      breadcrumb("connect");
      noteServerConnected();
      setReadyState(ReadyState.OPEN);
      setConnectionStatus("open");
      callHandler("onConnect", () => handlersRef.current.onConnect?.());
    };

    const onDisconnect = (reason: string) => {
      breadcrumb("disconnect", { reason }, "warning");
      setReadyState(ReadyState.CLOSED);
      callHandler("onDisconnect", () =>
        handlersRef.current.onDisconnect?.(reason)
      );
      if (reason === "io client disconnect") {
        cancelServerKickReconnect(false);
        inSocketIoBackoff = false;
        setConnectionStatus("closed");
        return;
      }
      setConnectionStatus("reconnecting");
      if (reason === "io server disconnect") {
        // Socket.IO does not reconnect on its own after the server kicked us;
        // retry with a backoff (see scheduleServerKickReconnect).
        inSocketIoBackoff = false;
        scheduleServerKickReconnect(socket);
      } else {
        // Socket.IO's own reconnection handles every other drop.
        inSocketIoBackoff = true;
        clearStableConnectionTimer();
      }
    };

    const onMessage = (msg: any) => {
      const parsed = parseSocketPayload(msg);
      if (parsed === undefined) return;
      data.current.push(createMessageEvent(parsed));
      callHandler("onMessage", () => handlersRef.current.onMessage?.(parsed));
    };

    const onServerError = (payload: any) => {
      breadcrumb("server error", {
        code: payload?.code,
        channel: payload?.channel,
      }, "warning");
      callHandler("onServerError", () =>
        handlersRef.current.onServerError?.(asObject<IWsServerError>(payload))
      );
    };

    const onSubscribed = (payload: unknown) =>
      callHandler("onServerAck", () =>
        handlersRef.current.onServerAck?.(asObject<IWsServerAck>(payload), "subscribed")
      );
    const onUnsubscribed = (payload: unknown) =>
      callHandler("onServerAck", () =>
        handlersRef.current.onServerAck?.(asObject<IWsServerAck>(payload), "unsubscribed")
      );

    // Listen for all events and add them to messages
    const onAnyEvent = (eventName: string, ...args: any[]) => {
      if (NON_CHANNEL_EVENTS.has(eventName)) return;
      const raw = args[0];
      const payload = typeof raw === "string" ? parseSocketPayload(raw) : raw;
      // An unparsable string frame is dropped (and reported by the parser).
      if (typeof raw === "string" && payload === undefined) return;
      const normalizedPayload =
        payload && typeof payload === "object" && !Array.isArray(payload)
          ? "channel" in payload
            ? payload
            : { channel: eventName, ...payload }
          : { channel: eventName, data: args };
      data.current.push(createMessageEvent(normalizedPayload));
    };

    const onReconnectAttempt = (attempt: number) => {
      breadcrumb("reconnect attempt", { attempt });
      setConnectionStatus("reconnecting");
    };

    const onReconnectFailed = () => {
      breadcrumb("reconnect failed", undefined, "warning");
      setConnectionStatus("closed");
    };

    const reconnectIfDown = (trigger: string) => {
      if (socket.connected) return;
      const kickPending = serverKickTimer !== null;
      if (!kickPending && !inSocketIoBackoff) {
        // First connect (or a closed socket): connect() opens it, and does
        // nothing while the manager is already opening.
        breadcrumb("reconnect now", { trigger });
        socket.connect();
        return;
      }
      // Skipping a backoff: at most one attempt per FORCED_RECONNECT_MIN_GAP_MS.
      const now = Date.now();
      if (now - lastForcedReconnectAt < FORCED_RECONNECT_MIN_GAP_MS) {
        breadcrumb("reconnect now throttled", { trigger });
        return;
      }
      lastForcedReconnectAt = now;
      breadcrumb("reconnect now", {
        trigger,
        skippedBackoff: kickPending ? "server kick" : "socket.io",
      });
      if (kickPending) {
        cancelServerKickReconnect(false);
        socket.connect();
        return;
      }
      // socket.io ignores connect() mid-backoff and its reconnect() is private.
      // disconnect() on a socket that is not connected emits no `disconnect`
      // and clears the pending backoff timer; connect() then opens at once.
      // Auth and subscriptions replay through onConnect as on any reconnect.
      socket.disconnect();
      socket.connect();
    };
    const onOnline = () => reconnectIfDown("online");
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") reconnectIfDown("visible");
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('message', onMessage);
    socket.on('error', onServerError);
    socket.on('subscribed', onSubscribed);
    socket.on('unsubscribed', onUnsubscribed);
    socket.onAny(onAnyEvent);
    socket.io.on('reconnect_attempt', onReconnectAttempt);
    socket.io.on('reconnect_failed', onReconnectFailed);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibilityChange);

    socketRef.current = socket;
    socketRefCount++;

    // Update ready state based on current socket state
    if (socket.connected) {
      setReadyState(ReadyState.OPEN);
      setConnectionStatus("open");
      callHandler("onConnect", () => handlersRef.current.onConnect?.());
    }

    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('message', onMessage);
      socket.off('error', onServerError);
      socket.off('subscribed', onSubscribed);
      socket.off('unsubscribed', onUnsubscribed);
      socket.offAny(onAnyEvent);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.io.off('reconnect_failed', onReconnectFailed);
      socketRefCount--;
      if (socketRefCount === 0 && sharedSocket) {
        resetReconnectState();
        sharedSocket.disconnect();
        sharedSocket = null;
      }
    };
  }, []);

  const sendMessage = useCallback((message: string | object) => {
    if (socketRef.current?.connected) {
      const parsed: any = parseSocketPayload(message, { reportInvalid: false });
      if (parsed?.op === "subscribe" && Array.isArray(parsed.data)) {
        parsed.data.forEach((channelData: string) => {
          socketRef.current?.emit("subscribe", subscribePayloadFor(channelData));
        });
        return;
      } else if (parsed?.op === "unsubscribe" && Array.isArray(parsed.data)) {
        parsed.data.forEach((channelData: string) => {
          socketRef.current?.emit("unsubscribe", unsubscribePayloadFor(channelData));
        });
        return;
      }
      socketRef.current.emit('message', message);
    }
  }, []);

  /**
   * Drop the server session and open a new one. Used to de-authenticate: the
   * backend binds the account to the socket, so a fresh socket is the only
   * logout an old server understands.
   */
  const reconnect = useCallback(() => {
    const socket = socketRef.current;
    if (!socket) return;
    breadcrumb("reconnect fresh session");
    socket.disconnect();
    socket.connect();
    setConnectionStatus("connecting");
  }, []);

  const resetState = useCallback(() => {
    if (sharedSocket) {
      resetReconnectState();
      sharedSocket.disconnect();
      sharedSocket = null;
      socketRefCount = 0;
      setReadyState(ReadyState.CLOSED);
      setConnectionStatus("closed");
    }
  }, []);

  const [messages, setMessages] = useState<MessageEvent<any>[] | null>(null);

  useEffect(() => {
    const flushMessages = () => {
      const { items, conflated, dropped, droppedChannels } = data.current.drain();
      if (items.length) {
        setMessages(items);
      }
      if (dropped) {
        breadcrumb(
          "frames dropped",
          { dropped, conflated, channels: droppedChannels },
          "warning"
        );
        // The dropped frames are gone; their owners refetch from REST (M2).
        callHandler("onFramesDropped", () =>
          handlersRef.current.onFramesDropped?.(droppedChannels)
        );
      }
    };
    const timer = setInterval(flushMessages, flushMessagesIntervalMs);

    return () => {
      clearInterval(timer);
      flushMessages();
    };
  }, []);

  return {
    readyState,
    connectionStatus,
    sendMessage,
    reconnect,
    lastMessages: messages,
    resetGlobalState: resetState,
  };
};

export default useSharedWebsocket;
